import React from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { colors } from "@/design-system/colors";
import "@/design-system/typography.css";
import '@/design-system/forms.css';

// ============================================================================
// GenericTabBar - Reusable tab bar component matching dashboard jobs status tabs
// ============================================================================

type TabItem = string | { value: string; label: string; tooltip?: string };

interface GenericTabBarProps {
  /**
   * Array of tab items. Can be:
   * - Simple string array: ["Tab 1", "Tab 2"] (value and label are the same)
   * - Object array: [{ value: "tab1", label: "Tab 1" }] (value and label differ)
   */
  tabs: TabItem[];
  
  /**
   * Currently active tab value (must match one of the tab values)
   */
  activeTab: string;
  
  /**
   * Callback function when a tab is clicked
   * @param value - The value of the clicked tab
   */
  onTabChange: (value: string) => void;
  
  /**
   * Optional additional className for the Tabs container
   */
  className?: string;

  /**
   * Optional children (e.g., TabsContent components) to render inside the Tabs wrapper
   */
  children?: React.ReactNode;

  /**
   * Color of the active tab's background. Defaults to 'white' (the
   * original dashboard jobs-status look, unchanged for every existing
   * caller). 'primary' uses KrewUp's real primary brand color --
   * design-system/colors.ts's `buttonSecondary` (blue, #3f51b5), NOT
   * `buttonPrimary` (orange, #ed5900) despite that constant's name.
   * `buttonPrimary`/`buttonSecondary` in colors.ts are legacy names from
   * when the orange "New Item" action was implemented first -- orange is
   * KrewUp's ACCENT/second color, blue is the real primary (confirmed
   * 2026-09-29). Centralized here rather than any caller hardcoding
   * either color itself.
   */
  activeVariant?: 'white' | 'primary';
}

/**
 * GenericTabBar Component
 * 
 * A reusable tab bar component that matches the exact styling from the dashboard
 * jobs status tabs (Prospect, Estimating, In Progress, Finishing, Completed).
 * 
 * Styling details:
 * - TabsList: bg-gray-100/80, rounded-md, h-10, p-1
 * - TabsTrigger (inactive): text-gray-600, form-text, rounded-sm, px-3, py-1.5
 * - TabsTrigger (active): bg-white (overrides default gray-300)
 * - Includes hover states, focus states, and transitions
 * 
 * @example
 * ```tsx
 * const [activeTab, setActiveTab] = useState("Tab 1");
 * 
 * <GenericTabBar
 *   tabs={["Tab 1", "Tab 2", "Tab 3"]}
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 * />
 * ```
 */
export function GenericTabBar({
  tabs,
  activeTab,
  onTabChange,
  className,
  children,
  activeVariant = 'white',
}: GenericTabBarProps) {
  return (
    <Tabs
      value={activeTab}
      onValueChange={onTabChange}
      // "manual" activation: Radix's default ("automatic") calls
      // onValueChange from BOTH the trigger's onMouseDown AND its onFocus
      // handler -- a real click fires focus right after mousedown, so
      // onTabChange ran twice per click. Harmless when it just switches
      // tabs, but when onTabChange can block (e.g. Settings' unsaved-
      // changes confirm), the second call re-ran the same confirm a
      // second time. "manual" keeps onFocus from ever calling
      // onValueChange -- only a real click/Enter/Space does, exactly once.
      activationMode="manual"
      className={cn("w-auto", className)}
    >
      <TabsList className="bg-gray-100/80">
        {tabs.map((tab) => {
          const value = typeof tab === 'string' ? tab : tab.value;
          const label = typeof tab === 'string' ? tab : tab.label;
          const tooltip = typeof tab === 'string' ? undefined : tab.tooltip;
          
          const isActive = value === activeTab;
          const trigger = (
            <TabsTrigger
              key={value}
              value={value}
              className={cn(
                activeVariant === 'primary' ? "data-[state=active]:text-white" : "data-[state=active]:bg-white"
              )}
              style={activeVariant === 'primary' && isActive ? { backgroundColor: colors.buttonSecondary } : undefined}
            >
              {label}
            </TabsTrigger>
          );

          if (tooltip) {
            return (
              <Tooltip key={value} content={tooltip}>
                {trigger}
              </Tooltip>
            );
          }

          return trigger;
        })}
      </TabsList>
      {children}
    </Tabs>
  );
}

